import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import {
  Badge, Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { currency, dayMonth } from '../../utils/format';
import { HOTEL_AMENITIES, ROOM_TYPES } from '../../constants';

const TABS = [
  { key: 'hotel', label: 'Hotel' },
  { key: 'rooms', label: 'Rooms' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'calendar', label: 'Calendar' },
];
const BLANK_ROOM = {
  title: '', roomType: 'double', price: '', mrp: '', ac: true,
  beds: '1', maxGuests: '2', totalRooms: '1', photos: [], description: '',
};

/** The owner's whole property: profile, rooms, the front desk and the calendar. */
const MyHotelPage = () => {
  const toast = useToast();
  const { location } = useApp();

  // Opened from the dashboard / nav on a given tab: ?tab=rooms, ?tab=bookings.
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'hotel');
  useEffect(() => { if (params.get('tab')) setTab(params.get('tab')); }, [params]);
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roomDraft, setRoomDraft] = useState(null);

  const [form, setForm] = useState({
    name: '', description: '', photos: [], phone: '', whatsapp: '',
    amenities: [], checkInTime: '13:00', checkOutTime: '11:00', rules: '',
    coupleFriendly: true, petsAllowed: false,
    freeUntilHours: '24', chargePercent: '50', offerLabel: '', offerPercent: '',
  });

  const fillForm = (h) => setForm({
    name: h.name || '',
    description: h.description || '',
    photos: h.photos || [],
    phone: h.phone || '',
    whatsapp: h.whatsapp || '',
    amenities: h.amenities || [],
    checkInTime: h.checkInTime || '13:00',
    checkOutTime: h.checkOutTime || '11:00',
    rules: (h.rules || []).join('\n'),
    coupleFriendly: Boolean(h.coupleFriendly),
    petsAllowed: Boolean(h.petsAllowed),
    freeUntilHours: String((h.cancellation && h.cancellation.freeUntilHours) ?? 24),
    chargePercent: String((h.cancellation && h.cancellation.chargePercent) ?? 50),
    offerLabel: (h.offer && h.offer.label) || '',
    offerPercent: h.offer && h.offer.percent ? String(h.offer.percent) : '',
  });

  const load = useCallback(() => {
    api.hotels.mine()
      .then((r) => {
        setHotel(r.data.hotel);
        setRooms(r.data.rooms || []);
        if (r.data.hotel) fillForm(r.data.hotel);
      })
      .catch(() => setHotel(null))
      .finally(() => setLoading(false));
    api.hotels.partnerBookings({ limit: 40 }).then((r) => setBookings(r.data || [])).catch(() => setBookings([]));
    api.hotels.calendar().then((r) => setCalendar(r.data)).catch(() => setCalendar(null));
    api.hotels.earnings().then((r) => setEarnings(r.data)).catch(() => setEarnings(null));
  }, []);
  useEffect(load, [load]);

  const saveHotel = async () => {
    setSaving(true);
    try {
      const res = await api.hotels.saveMine({
        name: form.name.trim(),
        description: form.description,
        photos: form.photos,
        phone: form.phone,
        whatsapp: form.whatsapp,
        amenities: form.amenities,
        checkInTime: form.checkInTime,
        checkOutTime: form.checkOutTime,
        rules: form.rules.split('\n').map((r) => r.trim()).filter(Boolean),
        coupleFriendly: form.coupleFriendly,
        petsAllowed: form.petsAllowed,
        cancellation: {
          freeUntilHours: Number(form.freeUntilHours) || 0,
          chargePercent: Number(form.chargePercent) || 0,
        },
        offer: { label: form.offerLabel, percent: Number(form.offerPercent) || 0 },
        coordinates: hotel && hotel.location && hotel.location.coordinates[0]
          ? undefined
          : [location.longitude, location.latitude],
      });
      setHotel(res.data);
      toast.success(res.message, 'Saved');
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally { setSaving(false); }
  };

  const saveRoom = async () => {
    const payload = {
      title: roomDraft.title.trim(),
      roomType: roomDraft.roomType,
      description: roomDraft.description,
      photos: roomDraft.photos,
      price: Number(roomDraft.price) || 0,
      mrp: Number(roomDraft.mrp) || 0,
      ac: roomDraft.ac,
      beds: Number(roomDraft.beds) || 1,
      maxGuests: Number(roomDraft.maxGuests) || 2,
      totalRooms: Number(roomDraft.totalRooms) || 1,
    };
    if (!payload.title || !payload.price) {
      toast.error('A room needs a name and a price per night.', 'Add the basics');
      return;
    }
    try {
      if (roomDraft._id) await api.hotels.updateRoom(roomDraft._id, payload);
      else await api.hotels.addRoom(payload);
      setRoomDraft(null);
      load();
    } catch (err) { toast.error(err.message, 'Could not save the room'); }
  };

  const removeRoom = async (room) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${room.title}?`)) return;
    await api.hotels.removeRoom(room._id);
    load();
  };

  const setStatus = async (booking, status) => {
    try {
      await api.hotels.setBookingStatus(booking._id, { status });
      load();
    } catch (err) { toast.error(err.message, 'Could not update'); }
  };

  const toggleAmenity = (value) => setForm((f) => ({
    ...f,
    amenities: f.amenities.includes(value) ? f.amenities.filter((a) => a !== value) : [...f.amenities, value],
  }));

  if (loading) return <Loading text="Loading your hotel…" />;

  return (
    <div style={{ maxWidth: 900 }}>
      <PageHeader
        title="My hotel"
        subtitle={hotel ? `${hotel.name} · ${hotel.status}` : 'Set up your property'}
        back
      />

      {hotel && hotel.status !== 'approved' ? (
        <div className="card" style={{ background: '#FEF3C7', marginBottom: 14 }}>
          <span className="t-small">
            {hotel.status === 'pending'
              ? 'Your property is with KAAMWALA for verification. Guests will see it once it is approved.'
              : `This property is ${hotel.status}. ${hotel.rejectionReason || ''}`}
          </span>
        </div>
      ) : null}

      {hotel ? (
        <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
          {TABS.map((t) => <Chip key={t.key} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />)}
        </div>
      ) : null}

      {tab === 'hotel' ? (
        <Reveal className="card sh-sm">
          <ImageUploader
            label="Hotel photos"
            hint="The front, the lobby, a room or two."
            folder="hotel"
            max={10}
            value={form.photos}
            onChange={(v) => setForm({ ...form, photos: v })}
          />
          <Input label="Hotel name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="About the hotel" textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input label="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Check-in time" type="time" value={form.checkInTime} onChange={(e) => setForm({ ...form, checkInTime: e.target.value })} />
            <Input label="Check-out time" type="time" value={form.checkOutTime} onChange={(e) => setForm({ ...form, checkOutTime: e.target.value })} />
          </div>

          <label className="field-label">Amenities</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            {HOTEL_AMENITIES.map((a) => (
              <Chip key={a.value} label={a.label} active={form.amenities.includes(a.value)} onClick={() => toggleAmenity(a.value)} />
            ))}
          </div>

          <Input
            label="Hotel rules"
            hint="One rule per line. Guests see these before booking."
            textarea
            rows={3}
            value={form.rules}
            onChange={(e) => setForm({ ...form, rules: e.target.value })}
          />

          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            <Chip label={form.coupleFriendly ? '✓ Couples welcome' : 'Couples welcome'} active={form.coupleFriendly} onClick={() => setForm({ ...form, coupleFriendly: !form.coupleFriendly })} />
            <Chip label={form.petsAllowed ? '✓ Pets allowed' : 'Pets allowed'} active={form.petsAllowed} onClick={() => setForm({ ...form, petsAllowed: !form.petsAllowed })} />
          </div>

          <label className="field-label">Cancellation</label>
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Free until (hours before)" inputMode="numeric" value={form.freeUntilHours} onChange={(e) => setForm({ ...form, freeUntilHours: e.target.value.replace(/\D/g, '') })} />
            <Input label="After that, keep (%)" inputMode="numeric" value={form.chargePercent} onChange={(e) => setForm({ ...form, chargePercent: e.target.value.replace(/\D/g, '') })} />
          </div>

          <label className="field-label">Offer (optional)</label>
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Label" placeholder="Monsoon deal" value={form.offerLabel} onChange={(e) => setForm({ ...form, offerLabel: e.target.value })} />
            <Input label="Discount %" inputMode="numeric" value={form.offerPercent} onChange={(e) => setForm({ ...form, offerPercent: e.target.value.replace(/\D/g, '') })} />
          </div>

          <Button title={hotel ? 'Save hotel' : 'Create hotel'} loading={saving} onClick={saveHotel} />
        </Reveal>
      ) : null}

      {tab === 'rooms' ? (
        <Reveal>
          {rooms.map((r) => (
            <div className="card sh-sm" key={r._id} style={{ marginBottom: 12 }}>
              <div className="row" style={{ gap: 12 }}>
                {(r.photos || []).length ? (
                  <img src={fileUrl(r.photos[0])} alt={r.title} style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover' }} />
                ) : null}
                <div className="grow">
                  <div className="t-title">{r.title}</div>
                  <div className="t-tiny">
                    {r.ac ? '❄️ AC' : 'Non-AC'} · sleeps {r.maxGuests} · {r.totalRooms} room{r.totalRooms > 1 ? 's' : ''}
                  </div>
                  <div className="t-body" style={{ fontWeight: 800 }}>{currency(r.price)} / night</div>
                </div>
                <div className="row" style={{ gap: 12 }}>
                  <button type="button" className="link-btn" onClick={() => setRoomDraft({
                    ...r,
                    price: String(r.price),
                    mrp: String(r.mrp || ''),
                    beds: String(r.beds),
                    maxGuests: String(r.maxGuests),
                    totalRooms: String(r.totalRooms),
                  })}
                  >
                    Edit
                  </button>
                  <button type="button" className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => removeRoom(r)}>Remove</button>
                </div>
              </div>
            </div>
          ))}

          {roomDraft ? (
            <div className="card sh-sm">
              <div className="t-title">{roomDraft._id ? 'Edit room' : 'New room'}</div>
              <ImageUploader label="Room photos" folder="hotel" max={6} value={roomDraft.photos} onChange={(v) => setRoomDraft({ ...roomDraft, photos: v })} />
              <Input label="Room name" placeholder="Deluxe double" value={roomDraft.title} onChange={(e) => setRoomDraft({ ...roomDraft, title: e.target.value })} />
              <label className="field-label">Room type</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                {ROOM_TYPES.map((t) => (
                  <Chip key={t.value} label={t.label} active={roomDraft.roomType === t.value} onClick={() => setRoomDraft({ ...roomDraft, roomType: t.value })} />
                ))}
              </div>
              <div className="split-2" style={{ gap: 12 }}>
                <Input label="Price / night" inputMode="numeric" value={roomDraft.price} onChange={(e) => setRoomDraft({ ...roomDraft, price: e.target.value.replace(/\D/g, '') })} />
                <Input label="Was (₹)" inputMode="numeric" value={roomDraft.mrp} onChange={(e) => setRoomDraft({ ...roomDraft, mrp: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div className="split-2" style={{ gap: 12 }}>
                <Input label="Sleeps" inputMode="numeric" value={roomDraft.maxGuests} onChange={(e) => setRoomDraft({ ...roomDraft, maxGuests: e.target.value.replace(/\D/g, '') })} />
                <Input label="Beds" inputMode="numeric" value={roomDraft.beds} onChange={(e) => setRoomDraft({ ...roomDraft, beds: e.target.value.replace(/\D/g, '') })} />
              </div>
              <Input label="How many rooms of this kind" inputMode="numeric" value={roomDraft.totalRooms} onChange={(e) => setRoomDraft({ ...roomDraft, totalRooms: e.target.value.replace(/\D/g, '') })} />
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                <Chip label={roomDraft.ac ? '✓ Air conditioned' : 'Air conditioned'} active={roomDraft.ac} onClick={() => setRoomDraft({ ...roomDraft, ac: !roomDraft.ac })} />
              </div>
              <div className="row" style={{ gap: 12 }}>
                <Button title="Save room" onClick={saveRoom} />
                <Button title="Cancel" variant="light" onClick={() => setRoomDraft(null)} />
              </div>
            </div>
          ) : (
            <Button title="+ Add a room" variant="ghost" onClick={() => setRoomDraft({ ...BLANK_ROOM })} />
          )}
        </Reveal>
      ) : null}

      {tab === 'bookings' ? (
        <Reveal>
          {earnings ? (
            <div className="card sh-sm" style={{ marginBottom: 12 }}>
              <div className="t-title">Earnings</div>
              <div className="t-tiny">{earnings.bookings} bookings · {earnings.nights} nights</div>
              <div className="t-body" style={{ fontWeight: 800 }}>{currency(earnings.net)} after commission</div>
              <div className="t-tiny">Gross {currency(earnings.gross)} · KAAMWALA {currency(earnings.commission)}</div>
            </div>
          ) : null}

          {!bookings.length ? (
            <Empty emoji="📅" title="No bookings yet" text="Guests who book a room show up here." />
          ) : bookings.map((b) => (
            <div className="card sh-sm" key={b._id} style={{ marginBottom: 12 }}>
              <div className="row-between">
                <div className="t-title">{b.guestName || (b.user && b.user.name)}</div>
                <Badge value={b.status.replace('_', ' ')} />
              </div>
              <div className="t-tiny">{b.bookingNo} · {b.guestPhone || (b.user && b.user.phone)}</div>
              <div className="t-small">
                {dayMonth(b.checkIn)} → {dayMonth(b.checkOut)} · {b.roomsBooked} × {b.room && b.room.title}
              </div>
              <div className="t-body" style={{ fontWeight: 800 }}>
                {currency(b.totalAmount)} · {b.paymentMode === 'cash' ? 'pay at hotel' : b.paymentStatus}
              </div>
              {b.specialRequests ? <div className="t-tiny">💬 {b.specialRequests}</div> : null}
              <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
                {['confirmed', 'checked_in', 'completed', 'rejected'].map((s) => (
                  <Chip key={s} label={s.replace('_', ' ')} active={b.status === s} onClick={() => setStatus(b, s)} />
                ))}
              </div>
            </div>
          ))}
        </Reveal>
      ) : null}

      {tab === 'calendar' && calendar ? (
        <Reveal className="card sh-sm">
          <div className="t-small">{calendar.totalRooms} rooms in total. Green means rooms are free.</div>
          {calendar.days.map((d) => (
            <div className="row-between row-line" key={d.date}>
              <span className="t-body">{dayMonth(d.date)}</span>
              <span className="t-tiny">{d.booked} booked</span>
              <span className="t-body" style={{ color: d.free ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                {d.free} free
              </span>
            </div>
          ))}
        </Reveal>
      ) : null}
    </div>
  );
};

export default MyHotelPage;
